import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppConst from '../apis/appconst';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function ProfitLoss() {
    const navigate = useNavigate();

    const getTodayDate = () => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    // ✅ Helper: ensure patient name shows "Walk-in" if blank/null
    const resolvePatientName = (raw) => {
        const v = (raw || '').toString().trim();
        return v === '' ? 'Walk-in' : v;
    };

    const [formData, setFormData] = useState({
        records: [],
        search_key: '',
        from_date: getTodayDate(),
        to_date: getTodayDate()
    });

    const [totals, setTotals] = useState({
        total_purchase: 0,
        total_sale: 0,
        total_profit: 0,
        total_loss: 0,
        net_profit: 0,
        margin_pct: 0,
        markup_pct: 0
    });

    const [loading, setLoading] = useState(false);
    const [image, setImage] = useState('');

    const fetchConfigData = async () => {
        try {
            const res = await axios.post(`${AppConst.BASE_URL + AppConst.getconfig}?config_type=lab`);
            if (res.data.status) {
                setImage(res.data.header_logo);
            }
        } catch (err) {
            console.error('Error fetching config:', err);
        }
    };

    const fetchProfitLossData = async () => {
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append('search_key', formData.search_key);
            fd.append('from_date', formData.from_date);
            fd.append('to_date', formData.to_date);

            const response = await axios.post(
                AppConst.BASE_URL + AppConst.getProfitLossReportUrl,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            if (response.data.status) {
                setFormData(prev => ({ ...prev, records: response.data.records }));
                setTotals({
                    total_purchase: response.data.totals.total_purchase || 0,
                    total_sale:     response.data.totals.total_sale     || 0,
                    total_profit:   response.data.totals.total_profit   || 0,
                    total_loss:     response.data.totals.total_loss     || 0,
                    net_profit:     response.data.totals.net_profit     || 0,
                    margin_pct:     response.data.totals.margin_pct     || 0,
                    markup_pct:     response.data.totals.markup_pct     || 0
                });
            } else {
                setFormData(prev => ({ ...prev, records: [] }));
                setTotals({
                    total_purchase: 0, total_sale: 0, total_profit: 0,
                    total_loss: 0, net_profit: 0, margin_pct: 0, markup_pct: 0
                });
            }
        } catch (error) {
            console.error('Error fetching profit/loss data:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch profit/loss data',
                confirmButtonColor: '#405189'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchConfigData();
        fetchProfitLossData();
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchProfitLossData();
        }, 300);
        return () => clearTimeout(timer);
    }, [formData.from_date, formData.to_date]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSearch = () => {
        fetchProfitLossData();
    };

    const handleClear = () => {
        setFormData(prev => ({
            ...prev,
            search_key: '',
            from_date: getTodayDate(),
            to_date: getTodayDate()
        }));
    };

    const imageUrlToBase64 = async (url) => {
        if (!url) return null;
        try {
            const response = await fetch(url);
            if (!response.ok) return null;
            const blob = await response.blob();
            if (!blob.type.startsWith('image/')) return null;
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error('Error converting image:', error);
            return null;
        }
    };

    const exportToPDF = async () => {
        try {
            Swal.fire({
                title: 'Generating PDF...',
                text: 'Please wait',
                allowOutsideClick: false,
                didOpen: () => Swal.showLoading()
            });

            const doc = new jsPDF('p', 'pt', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();
            const margin = 30;
            let y = 40;

            const headerImageBase64 = await imageUrlToBase64(image);
            if (headerImageBase64) {
                const headerImageHeight = 80;
                doc.addImage(headerImageBase64, 'PNG', 0, 0, pageWidth, headerImageHeight);
                y = headerImageHeight + 20;
            }

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(18);
            doc.text('Profit & Loss Report', pageWidth / 2, y, { align: 'center' });
            y += 20;

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.text(`Period: ${formatDate(formData.from_date)} to ${formatDate(formData.to_date)}`, pageWidth / 2, y, { align: 'center' });
            y += 20;

            const tableHeaders = [
                ['Sl#', 'Medicine', 'Customer', 'Batch', 'Qty', 'Pur. Price', 'Sale Price', 'Profit/Loss', 'Profit %']
            ];

            const tableBody = formData.records.map((record, index) => [
                index + 1,
                record.medicine_name || '-',
                resolvePatientName(record.customer_name),
                record.batch || '-',
                record.sale_qty,
                `${parseFloat(record.actual_purchase_price || 0).toFixed(2)}`,
                `${parseFloat(record.sale_price || 0).toFixed(2)}`,
                `${parseFloat(record.profit_loss || 0).toFixed(2)}`,
                `${parseFloat(record.profit_pct || 0).toFixed(2)}%`
            ]);

            autoTable(doc, {
                startY: y,
                head: tableHeaders,
                body: tableBody,
                theme: 'grid',
                styles: { fontSize: 9, cellPadding: 3 },
                headStyles: {
                    fillColor: [64, 81, 137],
                    textColor: 255,
                    fontStyle: 'bold'
                },
                columnStyles: {
                    0: { cellWidth: 25 },
                    1: { cellWidth: 110 },
                    2: { cellWidth: 75 },
                    3: { cellWidth: 55 },
                    4: { cellWidth: 35, halign: 'center' },
                    5: { cellWidth: 55, halign: 'right' },
                    6: { cellWidth: 55, halign: 'right' },
                    7: { cellWidth: 65, halign: 'right' },
                    8: { cellWidth: 55, halign: 'right' }
                },
                didParseCell: (data) => {
                    if (data.section === 'body' && (data.column.index === 7 || data.column.index === 8)) {
                        const record = formData.records[data.row.index];
                        if (record.is_profit) {
                            data.cell.styles.textColor = [0, 128, 0];
                            data.cell.styles.fontStyle = 'bold';
                        } else {
                            data.cell.styles.textColor = [255, 0, 0];
                            data.cell.styles.fontStyle = 'bold';
                        }
                    }
                }
            });

            y = doc.lastAutoTable.finalY + 20;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.text('Summary', margin, y);
            y += 15;

            const summaryData = [
                ['Total Purchase Amount', `${totals.total_purchase.toFixed(2)}`],
                ['Total Sale Amount',     `${totals.total_sale.toFixed(2)}`],
                ['Total Profit',          `${totals.total_profit.toFixed(2)}`],
                ['Total Loss',            `${totals.total_loss.toFixed(2)}`],
                ['Net Profit/Loss',       `${totals.net_profit.toFixed(2)}`],
                ['Margin % (on sales)',   `${totals.margin_pct.toFixed(2)}%`],
                ['Markup % (on cost)',    `${totals.markup_pct.toFixed(2)}%`]
            ];

            autoTable(doc, {
                startY: y,
                body: summaryData,
                theme: 'plain',
                styles: { fontSize: 11, cellPadding: 3 },
                columnStyles: {
                    0: { cellWidth: 180, fontStyle: 'bold' },
                    1: { cellWidth: 100, halign: 'right', fontStyle: 'bold' }
                },
                didParseCell: (data) => {
                    if (data.row.index === 4 || data.row.index === 5 || data.row.index === 6) {
                        if (totals.net_profit >= 0) {
                            data.cell.styles.textColor = [0, 128, 0];
                        } else {
                            data.cell.styles.textColor = [255, 0, 0];
                        }
                    }
                }
            });

            Swal.close();
            window.open(doc.output('bloburl'), '_blank');
        } catch (error) {
            Swal.close();
            console.error('PDF generation error:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to generate PDF',
                confirmButtonColor: '#405189'
            });
        }
    };

    const columns = [
        {
            name: 'Sl#',
            selector: (row, index) => index + 1,
            width: '70px',
            center: true
        },
        {
            name: 'Medicine Name',
            selector: row => row.medicine_name,
            sortable: true,
            wrap: true,
            minWidth: '200px'
        },
        {
            name: 'Customer Name',
            // ✅ Walk-in fallback applied here
            selector: row => resolvePatientName(row.customer_name),
            sortable: true,
            wrap: true,
            width: '150px',
            cell: row => {
                const name = resolvePatientName(row.customer_name);
                return name === 'Walk-in'
                    ? <span className="badge bg-secondary-subtle text-secondary">Walk-in</span>
                    : <span>{name}</span>;
            }
        },
        {
            name: 'Batch',
            selector: row => row.batch,
            sortable: true,
            width: '110px'
        },
        {
            name: 'Qty',
            selector: row => row.sale_qty,
            sortable: true,
            width: '70px',
            center: true
        },
        {
            name: 'Purchase Price',
            selector: row => row.actual_purchase_price,
            sortable: true,
            cell: row => `₹${parseFloat(row.actual_purchase_price || 0).toFixed(2)}`,
            right: true,
            width: '120px'
        },
        {
            name: 'Sale Price',
            selector: row => row.sale_price,
            sortable: true,
            cell: row => `₹${parseFloat(row.sale_price || 0).toFixed(2)}`,
            right: true,
            width: '110px'
        },
        {
            name: 'Profit/Loss',
            selector: row => row.profit_loss,
            sortable: true,
            cell: row => (
                <span style={{
                    color: row.is_profit ? '#28a745' : '#dc3545',
                    fontWeight: 'bold'
                }}>
                    ₹{parseFloat(row.profit_loss || 0).toFixed(2)}
                </span>
            ),
            right: true,
            width: '120px'
        },
        // ✅ Per-row profit %
        {
            name: 'Profit %',
            selector: row => row.profit_pct,
            sortable: true,
            cell: row => (
                <span style={{
                    color: row.is_profit ? '#28a745' : '#dc3545',
                    fontWeight: '600'
                }}>
                    {parseFloat(row.profit_pct || 0).toFixed(2)}%
                </span>
            ),
            right: true,
            width: '100px'
        },
        {
            name: 'Sale Date',
            selector: row => row.sale_date,
            sortable: true,
            cell: row => formatDate(row.sale_date),
            width: '120px'
        }
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#f1f5fb',
                borderBottom: '2px solid #dee2e6',
                fontSize: '14px',
                fontWeight: '700',
                color: '#405189'
            }
        },
        headCells: {
            style: {
                paddingLeft: '14px',
                paddingRight: '14px'
            }
        },
        rows: {
            style: {
                fontSize: '14px',
                minHeight: '50px'
            }
        },
        cells: {
            style: {
                paddingLeft: '14px',
                paddingRight: '14px'
            }
        }
    };

    return (
        <div className="page-content">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="card">
                            <div className="card-header border-0">
                                <div className="row align-items-center gy-3">
                                    <div className="col-sm">
                                        <h5 className="card-title mb-0" style={{ color: '#405189' }}>
                                            <i className="ri-line-chart-line me-2"></i>
                                            Profit & Loss Report
                                        </h5>
                                    </div>
                                    <div className="col-sm-auto">
                                        <button
                                            className="btn btn-danger"
                                            onClick={exportToPDF}
                                            disabled={formData.records.length === 0}
                                        >
                                            <i className="ri-file-pdf-line me-1"></i>
                                            Export to PDF
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="card-body border border-dashed border-end-0 border-start-0">
                                <form>
                                    <div className="row g-3">
                                        <div className="col-md-3">
                                            <label className="form-label">From Date</label>
                                            <input
                                                className="form-control"
                                                type="date"
                                                name="from_date"
                                                onChange={handleChange}
                                                value={formData.from_date}
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label">To Date</label>
                                            <input
                                                className="form-control"
                                                type="date"
                                                name="to_date"
                                                onChange={handleChange}
                                                value={formData.to_date}
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <label className="form-label">Search</label>
                                            <input
                                                className="form-control"
                                                type="text"
                                                name="search_key"
                                                placeholder="Search medicine, customer, batch..."
                                                onChange={handleChange}
                                                value={formData.search_key}
                                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleSearch())}
                                            />
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label">&nbsp;</label>
                                            <button
                                                type="button"
                                                onClick={handleClear}
                                                className="btn btn-info w-100"
                                            >
                                                <i className="ri-refresh-line me-1"></i> Clear
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>

                            <div className="card-body pt-0">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <DataTable
                                        columns={columns}
                                        data={formData.records}
                                        pagination
                                        paginationPerPage={25}
                                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                        highlightOnHover
                                        striped
                                        responsive
                                        customStyles={customStyles}
                                        noDataComponent={
                                            <div className="text-center py-5">
                                                <i className="ri-file-list-line fs-1 text-muted"></i>
                                                <p className="mt-3">No profit/loss data found</p>
                                            </div>
                                        }
                                    />
                                )}

                                {/* ════════════════════════════════════════════
                                    SUMMARY FOOTER — now with profit % & margin
                                    ════════════════════════════════════════════ */}
                                <div className="mt-4 p-4 bg-light rounded">
                                    <h6 className="mb-3">
                                        <i className="ri-file-chart-line me-2"></i>
                                        Summary
                                    </h6>
                                    <div className="row text-center g-3">
                                        <div className="col-md-2">
                                            <strong className="text-muted small">Total Purchase</strong>
                                            <div className="fs-5 text-primary">₹{totals.total_purchase.toFixed(2)}</div>
                                        </div>
                                        <div className="col-md-2">
                                            <strong className="text-muted small">Total Sale</strong>
                                            <div className="fs-5 text-info">₹{totals.total_sale.toFixed(2)}</div>
                                        </div>
                                        <div className="col-md-2">
                                            <strong className="text-muted small">Total Profit</strong>
                                            <div className="fs-5 text-success">₹{totals.total_profit.toFixed(2)}</div>
                                        </div>
                                        <div className="col-md-2">
                                            <strong className="text-muted small">Total Loss</strong>
                                            <div className="fs-5 text-danger">₹{totals.total_loss.toFixed(2)}</div>
                                        </div>
                                        <div className="col-md-4">
                                            <strong className="text-muted small">Net Profit/Loss</strong>
                                            <div
                                                className="fs-4"
                                                style={{
                                                    color: totals.net_profit >= 0 ? '#28a745' : '#dc3545',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                ₹{totals.net_profit.toFixed(2)}
                                            </div>
                                        </div>
                                    </div>

                                    {/* ✅ NEW: Profit % row */}
                                    <div className="row text-center g-3 mt-2 pt-3 border-top">
                                        <div className="col-md-6">
                                            <strong className="text-muted">Margin % (on Sales):</strong>
                                            <span
                                                className="fs-5 ms-2"
                                                style={{
                                                    color: totals.margin_pct >= 0 ? '#28a745' : '#dc3545',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {totals.margin_pct.toFixed(2)}%
                                            </span>
                                            <small className="text-muted d-block">Net Profit ÷ Total Sales</small>
                                        </div>
                                        <div className="col-md-6">
                                            <strong className="text-muted">Markup % (on Cost):</strong>
                                            <span
                                                className="fs-5 ms-2"
                                                style={{
                                                    color: totals.markup_pct >= 0 ? '#0d6efd' : '#dc3545',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {totals.markup_pct.toFixed(2)}%
                                            </span>
                                            <small className="text-muted d-block">Net Profit ÷ Total Purchase</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProfitLoss;